import Controller, { OnAfterFetch, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { BrokerService, LanguageService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { FetchEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { Inject } from '@odinjs/odin';
import moment from 'moment';
import BaseController from '../../BaseController';
import { InformationBarAlerts, InformationBarAlertType, InformationBarAPI, InformationBarField, InformationBarHighlighted } from '@philips-internal/oswald-ui-components';
import InformationBarUtils from './information-bar-header-utils';
import { FragmentNames, InformationBarHeaderAliases } from '../../../codegen/index';
import { isEmpty } from 'lodash';
import './information-bar-header.css';
import timeService from '../../../services/time-service';
import { DATE_PATTERN, FULL_LOCALE_DATE } from '../../../constants/date-utils';
import { InformationBarData } from '../../../interfaces/information-bar-data';
import { DataContext } from '@philips-internal/oswald-ui-core/lib/component';

const colombiaLocale = 'es-co';

@Controller({ fragment: FragmentNames.INFORMATION_BAR_HEADER })
class InformationBarHeaderController extends BaseController {

  @Inject(InformationBarHeaderAliases.INFORMATION_BAR)
    informationBar!: InformationBarAPI;

  collapsedValue!: boolean;

  formatDate( fullDate: string, dateFormat: string = FULL_LOCALE_DATE): string {
    if ( !fullDate ) {
      return '';
    }

    return moment(fullDate).format(dateFormat);
  }

  @OnMount({ target: InformationBarHeaderAliases.FETCH_PATIENT })
  onMount(): void {
    const { encounterId } = this.historyService.getCurrentRouteParams();
    this.context.set({ encounterId: encounterId });

    timeService.getCurrentDateTime()
      .then(currentDateTime => {
        const referenceDateTime = moment(currentDateTime).format(FULL_LOCALE_DATE);
        this.context.set({ currentDateTime: referenceDateTime })
      })
      .catch(() => this.context.set({ currentDateTime: moment().format(FULL_LOCALE_DATE) }));
  }

  @OnAfterFetch(InformationBarHeaderAliases.FETCH_PATIENT)
  onAfterFechEncounter(e: FetchEvent): void {
    const data = e.data as InformationBarData;

    if (!data) return;

    const { daysEntryQtt, encounterSectorCode } = data;
    const extraInfos = this.informationBar.getExtraInformation() as Record<string, InformationBarField>;
    const entryDateFormat = this.formatDate(extraInfos['DT_ENTRADA'].value as string);
    const formatBirthDate = this.formatDate(this.informationBar.getFieldOneValue() as string, DATE_PATTERN);
    const dischargedDate = this.formatDate(data?.dischargedDate as string);
    const deathDate = this.formatDate(data?.deathDate as string);
    const age = data?.age;
    const discharged = deathDate || dischargedDate;
    const information = data?.allergies?.allergies as unknown as InformationBarHighlighted[];
    const highlightedList = { information, title: 'tws.information-bar.allergies' };

    this.updateExtraInfos(extraInfos, entryDateFormat, daysEntryQtt, formatBirthDate);
    this.updateIdentification(extraInfos, LanguageService.getCurrentLanguage());

    this.informationBar.setExtraInformation(extraInfos);
    this.informationBar.setCollapsed(InformationBarUtils.collapsedBar);
    if (data?.allergies?.allergies) {
      this.informationBar.setHighlighted(highlightedList);
    }

    if (discharged) {
      this.updateDischargedInfo(extraInfos, formatBirthDate);
    }

    InformationBarUtils.clean();
    InformationBarUtils.encounterSectorCode = encounterSectorCode;
    InformationBarUtils.age = age;

    if (deathDate) {
      this.setDeceasedInfo(deathDate);
      return;
    }

    if (dischargedDate) {
      this.setDischargedInfo(dischargedDate);
      return;
    }

    this.informationBar.setFieldOneValue(formatBirthDate);
    this.informationBar.setAlerts(this.getAlerts(data));
    BrokerService.subscribeToTopic('setInformationBarData' , (context: DataContext) => { context.set({ informationBarData : data }); });
  }

  private updateExtraInfos(extraInfos: Record<string, InformationBarField>, entryDateFormat: string, daysEntryQtt: string, formatBirthDate: string): void {
    extraInfos['DT_ENTRADA'] = {
      label: extraInfos['DT_ENTRADA']?.label,
      value: `${entryDateFormat}  ${daysEntryQtt}`,
      visible: extraInfos['DT_ENTRADA']?.visible
    };

    const birthDate = extraInfos['DT_BIRTH'];
    if (birthDate) {
      extraInfos['DT_BIRTH'] = {
        label: birthDate?.label,
        value: `${formatBirthDate}`,
        visible: birthDate?.visible
      };
    }
  }

  private updateIdentification(extraInfos: Record<string, InformationBarField>, userLocale: string): void {
    if (extraInfos['NR_SEQ_TIPO_DOC_IDENTIFICACAO']) {
      const label = extraInfos['NR_SEQ_TIPO_DOC_IDENTIFICACAO'].label;
      const value = extraInfos['NR_SEQ_TIPO_DOC_IDENTIFICACAO'].value;
      extraInfos['NR_SEQ_TIPO_DOC_IDENTIFICACAO'] = {
        label: label,
        value: value,
        visible: colombiaLocale === userLocale && !(isEmpty(value) && isEmpty(label))
      };
    }
  }

  private updateDischargedInfo(extraInfos: Record<string, InformationBarField>, formatBirthDate: string): void{
    extraInfos['DT_BIRTH'] = {
      label: extraInfos['DT_BIRTH'].label,
      value: `${formatBirthDate}`,
      visible: extraInfos['DT_BIRTH'].visible
    };

    this.informationBar.setExtraInformation(extraInfos);
  }

  private setDeceasedInfo(deathDate: string): void{
    this.informationBar.setFieldOneTitle('text.deceased');
    this.informationBar.setFieldOneValue(deathDate);
    this.informationBar.setFieldOneHighlight(true);
  }

  private setDischargedInfo(dischargedDate: string): void {
    this.informationBar.setFieldOneTitle('text.discharged-date');
    this.informationBar.setFieldOneValue(dischargedDate);
    this.informationBar.setFieldOneHighlight(false);
  }

  private getAlerts(informations: InformationBarData): Record<string, InformationBarAlerts> {
    const alerts = informations.alerts;

    const amountActionsMentor = informations?.amountActionsMentor || 0;
    if (amountActionsMentor > 0) {
      const descriptionActionsMentor = `${MessageService.translate('txt.actions-mentor')} (${amountActionsMentor})`
      alerts.customAlert = this.generateCustomAlert([descriptionActionsMentor]);
    }

    if (informations.alerts?.isolation) {
      alerts.isolation = this.generateIsolationAlert(informations);
    }

    return alerts as Record<string, InformationBarAlerts>;
  }

  private generateIsolationAlert(informations: InformationBarData): InformationBarAlerts {
    const itemsDesc = informations.precautions?.precautionDescription.split(',') as string[];
    return {
      description: informations.alerts.isolation as unknown as string ?? '',
      highlight: true,
      items: itemsDesc,
      type: InformationBarAlertType.ICON,
    };
  }

  private generateCustomAlert(items: string[]): InformationBarAlerts {
    return {
      description: 'tws.information-bar.alerts',
      icon: '/static/assets/alerts.svg',
      iconClassName: 'c-information-bar__icon-alert',
      items: items,
      type: InformationBarAlertType.BADGE
    };
  }
}

export default InformationBarHeaderController;
